import React, { useState, useEffect } from "react";
import "./ListStyles.css";
import "./ConfirmDialog.css";
import "./FormStyles.css";
import useSessionStorage from "./useSessionStorage";

import { useNavigate } from "react-router-dom";

import useAxios from "./useAxios";

const PlayersList = ({ logout }) => {
  const navigate = useNavigate();
  const [axios] = useAxios(true, logout);

  const [tenant] = useSessionStorage("tenant");

  const [players, setPlayers] = useState([]);
  const [handicapTypes, setHandicapTypes] = useState([]);

  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSourceId, setMergeSourceId] = useState("");
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeError, setMergeError] = useState("");
  const [mergeLoading, setMergeLoading] = useState(false);

  useEffect(() => {
    axios.get("gametypes", setHandicapTypes);
    axios.get("players", setPlayers);
  }, []);

  const openMergeModal = () => {
    setMergeSourceId("");
    setMergeTargetId("");
    setMergeError("");
    setMergeLoading(false);
    setShowMergeModal(true);
  };

  const closeMergeModal = () => {
    if (!mergeLoading) setShowMergeModal(false);
  };

  const handleMerge = () => {
    setMergeLoading(true);
    setMergeError("");
    axios.post(
      "player/merge",
      { sourcePlayerId: parseInt(mergeSourceId), targetPlayerId: parseInt(mergeTargetId) },
      () => {
        setShowMergeModal(false);
        setMergeLoading(false);
        axios.get("players", setPlayers);
      },
      () => {
        setMergeError("Merge failed. Please try again.");
        setMergeLoading(false);
      }
    );
  };

  const sourcePlayer = players.find((p) => p.id === parseInt(mergeSourceId));
  const targetPlayer = players.find((p) => p.id === parseInt(mergeTargetId));

  return (
    <div className="list-container">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0 }}>Players</h2>
        <button
          className="edit-button"
          aria-label="Add New Player"
          style={{
            fontSize: 24,
            padding: "4px 14px",
            margin: 0,
            lineHeight: 1,
          }}
          onClick={() => navigate("/configuration/player/0")}
        >
          +
        </button>
        <button
          className="edit-button"
          style={{ margin: 0 }}
          onClick={openMergeModal}
        >
          Merge
        </button>
      </div>

      {/* Desktop table view */}
      <table className="list-table desktop-table">
        <thead>
          <tr>
            {tenant.super ? <th>Tenant</th> : null}
            <th>Name</th>
            {handicapTypes.map((type) => (
              <th key={type.id}>{type.description} Handicap</th>
            ))}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {players.map((player) => (
            <tr key={player.id}>
              {tenant.super ? <td>{player.tenant.name}</td> : null}
              <td>{player.name}</td>
              {handicapTypes.map((type) => {
                const handicap = player.handicaps?.find(h => h.gameTypeId === type.id);
                return <td key={type.id}>{handicap?.value || 0}</td>;
              })}
              <td>
                <button
                  className="edit-button"
                  onClick={() => navigate("/configuration/player/" + player.id)}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile card view */}
      <div className="mobile-cards">
        {players.map((player) => (
          <div key={player.id} className="mobile-card">
            <div className="mobile-card-header">
              <h3>{player.name}</h3>
            </div>
            <div className="mobile-card-body">
              {tenant.super && (
                <div className="mobile-card-row">
                  <span className="label">Tenant:</span>
                  <span>{player.tenant.name}</span>
                </div>
              )}
              {handicapTypes.map((type) => {
                const handicap = player.handicaps?.find(h => h.gameTypeId === type.id);
                return (
                  <div key={type.id} className="mobile-card-row">
                    <span className="label">{type.description} Handicap:</span>
                    <span>{handicap?.value || 0}</span>
                  </div>
                );
              })}
            </div>
            <div className="mobile-card-actions">
              <button
                className="edit-button"
                onClick={() => navigate("/configuration/player/" + player.id)}
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Merge Players Modal */}
      {showMergeModal && (
        <div className="confirm-dialog-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeMergeModal(); }}>
          <div className="confirm-dialog">
            <div className="confirm-dialog-header">
              <h3 className="confirm-dialog-title">Merge Players</h3>
            </div>
            <div className="confirm-dialog-content">
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 600, marginBottom: 6 }}>
                  Merge (will be deleted):
                </label>
                <select
                  className="form-control"
                  value={mergeSourceId}
                  onChange={(e) => setMergeSourceId(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: "1em" }}
                >
                  <option value="">Select player to delete...</option>
                  {players
                    .filter((p) => p.id !== parseInt(mergeTargetId))
                    .map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 600, marginBottom: 6 }}>
                  Into (will be kept):
                </label>
                <select
                  className="form-control"
                  value={mergeTargetId}
                  onChange={(e) => setMergeTargetId(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: "1em" }}
                >
                  <option value="">Select player to keep...</option>
                  {players
                    .filter((p) => p.id !== parseInt(mergeSourceId))
                    .map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
              </div>
              {sourcePlayer && targetPlayer && (
                <div className="confirm-dialog-details" style={{ background: "#fff3cd", borderColor: "#ffc107", marginTop: 8 }}>
                  <p style={{ margin: 0, color: "#856404" }}>
                    All records for <strong>{sourcePlayer.name}</strong> will be transferred to <strong>{targetPlayer.name}</strong>.
                    <strong> {sourcePlayer.name}</strong> will then be permanently deleted. This cannot be undone.
                  </p>
                </div>
              )}
              {mergeError && (
                <p style={{ color: "#dc3545", marginTop: 12 }}>{mergeError}</p>
              )}
            </div>
            <div className="confirm-dialog-buttons">
              <button
                type="button"
                className="confirm-dialog-cancel"
                onClick={closeMergeModal}
                disabled={mergeLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-dialog-confirm danger"
                disabled={!mergeSourceId || !mergeTargetId || mergeLoading}
                onClick={handleMerge}
              >
                {mergeLoading ? "Merging..." : "Merge"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayersList;
